import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import { FitnessCenter } from "@mui/icons-material";
import GoogleHealthDataDisclosure from "./GoogleHealthDataDisclosure";
import {
  fitbitCollectionAuthorizationStartUrl,
  fitbitCollectionProfileResumeStartUrl,
} from "../../services/fitbitCollectionEnrollmentService";

// Google's own page for linking a Fitbit account to a Google account.
const GOOGLE_HEALTH_SIGNUP_URL = "https://fitbit.google.com/auth/signup";

type FitbitCollectionInvitationStartContext = {
  kind: "initial_authorization" | "profile_resume";
  invitationToken: string;
};

export function invitationStartFromFragment(hash: string): FitbitCollectionInvitationStartContext | null {
  const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
  const parameters = new URLSearchParams(fragment);
  if (parameters.getAll("fitbitInvitation").length > 1 ||
    parameters.getAll("fitbitProfileResumeInvitation").length > 1) return null;
  const authorizationToken = parameters.get("fitbitInvitation")?.trim() || null;
  const profileResumeToken = parameters.get("fitbitProfileResumeInvitation")?.trim() || null;
  if ((authorizationToken === null && profileResumeToken === null) ||
    (authorizationToken !== null && profileResumeToken !== null)) {
    return null;
  }
  if (profileResumeToken !== null) {
    if (!/^[A-Za-z0-9_-]{43}$/.test(profileResumeToken)) return null;
    return { kind: "profile_resume", invitationToken: profileResumeToken };
  }
  if (!/^[A-Za-z0-9_-]{43}$/.test(authorizationToken as string)) return null;
  return { kind: "initial_authorization", invitationToken: authorizationToken as string };
}

export function invitationTokenFromFragment(hash: string): string | null {
  return invitationStartFromFragment(hash)?.invitationToken || null;
}

/**
 * Fresh Fitbit invitation landing. It consumes the opaque invitation from the
 * fragment, removes it from browser history, and explains data access before
 * the participant chooses to start OAuth. A normal form POST lets
 * the API issue either its short-lived OAuth cookie or its sealed
 * profile-completion cookie, according to the invitation type.
 */
const FitbitCollectionInvitationStart: React.FC = () => {
  const initialInvitation = useRef(invitationStartFromFragment(window.location.hash));
  const [invitation] = useState(initialInvitation.current);
  const formRef = useRef<HTMLFormElement>(null);
  const submitted = useRef(false);
  const [openingAuthorization, setOpeningAuthorization] = useState(false);
  const [failureReason] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("reason"));

  useEffect(() => {
    // Coming back from Google with the browser's Back button restores this
    // page as it was left; let the person continue again from here.
    const restore = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      submitted.current = false;
      setOpeningAuthorization(false);
    };
    window.addEventListener("pageshow", restore);
    return () => window.removeEventListener("pageshow", restore);
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(
        window.history.state,
        document.title,
        `${window.location.pathname}${window.location.search}`,
      );
    }
    if (invitation?.kind === "profile_resume" && formRef.current && !submitted.current) {
      // Resume an existing connection once. Fresh authorization must wait for
      // the participant to read the disclosure and explicitly continue.
      submitted.current = true;
      formRef.current.requestSubmit();
    }
  }, [invitation]);

  if (!invitation) {
    const authorizationFailed = window.location.pathname === "/fitbit/enroll/unavailable";
    const notLinked = authorizationFailed && failureReason === "google_health_not_linked";
    return (
      <Container maxWidth="sm">
        <Box sx={{ py: 6 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
              {notLinked ? "Your Google account isn't set up for Fitbit yet"
                : authorizationFailed ? "We could not complete authorization" : "Open your personal invitation"}
            </Typography>
            <Alert severity="info">
              {notLinked ? (
                <>The Google account you chose isn't linked to Fitbit. Set up Fitbit with that Google
                  account, then open the link in your study invitation email again. If you have more than
                  one Google account, choose the one you use with Fitbit.</>
              ) : authorizationFailed && failureReason === "unfinished_enrollments" ? (
                <>Other study enrollments are still open in this browser. Finish them, or wait 30 minutes,
                  then open the link in your study invitation email again.</>
              ) : (
                <>{authorizationFailed && "Your invitation may no longer be available, or Fitbit authorization was not completed. "}
                  To connect Fitbit, open the personal link in your study invitation email.
                  It identifies your study so you do not need to enter a study code or an email verification code.
                  If your link is unavailable or expired, ask the research team to send a new invitation.</>
              )}
            </Alert>
            {notLinked && (
              <Button variant="contained" href={GOOGLE_HEALTH_SIGNUP_URL} target="_blank" rel="noopener noreferrer"
                sx={{ mt: 3 }}>
                Set up Fitbit with Google
              </Button>
            )}
          </Paper>
        </Box>
      </Container>
    );
  }

  const isProfileResume = invitation.kind === "profile_resume";
  const action = isProfileResume
    ? fitbitCollectionProfileResumeStartUrl()
    : fitbitCollectionAuthorizationStartUrl();

  return (
    <Container maxWidth={isProfileResume ? "sm" : "md"}>
      <Box sx={{ py: 6 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
          <FitnessCenter sx={{ fontSize: 56, color: "primary.main", mb: 2 }} />
          {isProfileResume && <Box sx={{ mb: 2 }}><CircularProgress aria-label="Opening your invitation" /></Box>}
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
            {isProfileResume ? "Opening your profile" : "Connect Fitbit to your study"}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            {isProfileResume
              ? "Your invitation is being checked. You will continue to complete your StudySync profile."
              : "Review how StudySync uses your data before choosing your permissions with Google."}
          </Typography>
          {!isProfileResume && <GoogleHealthDataDisclosure />}
          <Box
            component="form"
            ref={formRef}
            aria-label={isProfileResume ? "Resume Fitbit profile" : "Start Fitbit authorization"}
            method="post"
            action={action}
            encType="application/x-www-form-urlencoded"
            onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
              if (isProfileResume) return;
              if (submitted.current) {
                event.preventDefault();
                return;
              }
              submitted.current = true;
              setOpeningAuthorization(true);
            }}
          >
            <input type="hidden" name="invitationToken" value={invitation.invitationToken} />
            <Button type="submit" variant="contained" size="large" disabled={openingAuthorization}>
              {isProfileResume ? "Resume profile" : openingAuthorization ? "Opening Google…" : "Continue to Google"}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {isProfileResume
                ? "If you are not redirected automatically, use the button above."
                : "You can cancel on Google's screen. After authorization, return here to complete your study profile."}
            </Typography>
            {!isProfileResume && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Google may say it hasn't verified StudySync yet. To continue, choose Advanced, then Go to StudySync.
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default FitbitCollectionInvitationStart;
